<template>
  <div class="yhc-ef-zip-input">
    <div
      v-if="title"
      class="yhc-ef-zip-input--title text-center bold"
    >
      {{ title }}
    </div>
    <div class="yhc-ef-zip-input--inner">
      <div class="zip-input--inner-instruction">
        <span class="bold">郵便番号を入力してください</span
        ><span class="text small"> ※ハイフン(-)不要</span>
      </div>
      <div class="yhc-ef-zip-input--field-wrap">
        <input
          :id="zipInputId"
          type="tel"
          pattern="[0-9]*"
          inputmode="numeric"
          maxlength="7"
          class="yhc-ef-zip-input--input form-control"
          :class="{
            'border border-danger': !!errorMessage,
          }"
          :value="modelValue"
          :aria-expanded="historyPanelOpen ? 'true' : 'false'"
          :aria-controls="historyListboxId"
          aria-autocomplete="list"
          placeholder="郵便番号を入力"
          @change="(_ev) => changed(_ev.target.value)"
          @focus="onZipFocus"
          @blur="onZipBlur"
        />
        <div
          v-show="historyPanelOpen && historyItems.length > 0"
          :id="historyListboxId"
          class="yhc-ef-zip-input__history"
          role="listbox"
          aria-label="最近入力した郵便番号"
        >
          <button
            v-for="item in historyItems"
            :key="item.zip + '-' + item.usedAt"
            type="button"
            class="yhc-ef-zip-input__history-item"
            role="option"
            @click="onHistorySelect(item)"
          >
            <span class="yhc-ef-zip-input__history-zip">{{ item.zip }}</span>
            <span class="yhc-ef-zip-input__history-address">{{ item.address }}</span>
          </button>
        </div>
        <span
          v-if="errorMessage"
          class="small text-danger"
          >{{ errorMessage }}</span
        >
      </div>
      <div class="mt-3 zipcode-guide-content">
        <a
          class="d-block small zipcode-guide-link bold"
          href="https://www.post.japanpost.jp/zipcode/"
          target="_blank"
          >郵便番号がわからない方はこちら</a
        >
      </div>
      <div
        v-if="addressString"
        class="result-address-contents mt-3"
      >
        <p class="result-address-title bold">ご住所</p>
        <p class="result-address-text">{{ addressString }}</p>
      </div>
      <div
        v-if="isOutOfServiceArea"
        class="small text-danger bold mt-2"
      >
        ※サービスエリア外のため、ご利用いただけません。
      </div>
      <div
        v-else-if="isRemoteIsland"
        class="small text-danger mt-2"
      >
        ※離島エリアです、通常より輸送日数がかかります。
      </div>
      <div
        v-if="serviceLevelNotification"
        class="small text-danger mt-2"
      >
        <span v-html="serviceLevelNotification"></span>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { PropType } from 'vue'
import type { YhcZipHistoryEntry } from '../../utils/yhcZipCodeHistory'
import {
  loadZipCodeHistory,
  recordZipCodeHistory,
  normalizeZipDigits,
} from '../../utils/yhcZipCodeHistory'
import {
  ModelYhcMastersByZipcode,
  YhcServiceLevelNotification,
} from '../../../../fireModels/yhcDataTypes'
import { YhcEstimateFormFrontService } from '../../YhcEstimateFormFrontService'
import { yhcAreasByCode } from '../../../../constants/area'
import { $yhcGA4EventSender, YhcZipCodePrefectureType } from '../../YhcGA4EventSender'
export default {
  name: 'YhcEFZipCodeInput',
  props: {
    fromOrTo: {
      type: String as PropType<'from' | 'to'>,
      required: true,
    },
    zipCodeMaster: {
      type: Object as PropType<ModelYhcMastersByZipcode | null>,
      required: false,
      default: null,
    },
    title: {
      type: String,
      required: false,
    },
    modelValue: {
      type: String,
      required: true,
    },
    isServiceLevel: {
      type: Boolean,
      required: false,
    },
  },
  inject: ['YhcEstimateFormFrontServiceInstance'],
  data() {
    return {
      awaitingGa4: false,
      isConfirmingZip: false,
      serviceLevelNotices: [] as YhcServiceLevelNotification[],
      historyPanelOpen: false,
      historyItems: [] as YhcZipHistoryEntry[],
      blurCloseTimer: null as ReturnType<typeof setTimeout> | null,
      historyListboxId: `yhc-zip-hist-${Math.random().toString(36).slice(2, 11)}`,
      zipInputId: `yhc-zip-in-${Math.random().toString(36).slice(2, 11)}`,
    }
  },
  watch: {
    modelValue() {
      this.$nextTick(() => this.tryRecordZipHistory())
    },
    zipCodeMaster: {
      handler() {
        this.$nextTick(() => this.tryRecordZipHistory())
      },
      deep: true,
    },
    displayZipMaster: {
      handler() {
        this.onDisplayZipMasterSettled()
      },
    },
    isLookupPending(isPending: boolean, wasPending: boolean) {
      if (wasPending && !isPending) {
        this.isConfirmingZip = false
      }
      this.onDisplayZipMasterSettled()
    },
  },
  unmounted() {
    this.clearHistoryBlurTimer()
  },
  methods: {
    clearHistoryBlurTimer() {
      if (this.blurCloseTimer != null) {
        clearTimeout(this.blurCloseTimer)
        this.blurCloseTimer = null
      }
    },
    refreshZipHistoryItems() {
      this.historyItems = loadZipCodeHistory(this.fromOrTo)
    },
    onZipFocus() {
      this.clearHistoryBlurTimer()
      this.refreshZipHistoryItems()
      this.historyPanelOpen = this.historyItems.length > 0
    },
    onZipBlur() {
      this.blurCloseTimer = window.setTimeout(() => {
        this.historyPanelOpen = false
        this.blurCloseTimer = null
      }, 220)
    },
    onHistorySelect(item: YhcZipHistoryEntry) {
      this.clearHistoryBlurTimer()
      this.historyPanelOpen = false
      this.changed(item.zip)
    },
    tryRecordZipHistory() {
      const zip = normalizeZipDigits(this.modelValue || '')
      if (zip.length !== 7 || !this.displayZipMaster || !this.addressString) {
        return
      }
      if (normalizeZipDigits(this.displayZipMaster.zip || '') !== zip) {
        return
      }
      recordZipCodeHistory(this.fromOrTo, zip, this.addressString)
    },
    changed(value) {
      // TODO: validate value
      // 全角 to 半角 => 数値以外の文字を削除
      value = value
        .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
        .replace(/[^0-9]/g, '')
      this.awaitingGa4 = !this.isServiceLevel
      this.isConfirmingZip = value.length === 7
      this.$emit('update:modelValue', value)
      this.$nextTick(() => this.onDisplayZipMasterSettled())
    },
    onDisplayZipMasterSettled() {
      const master = this.displayZipMaster
      if (!master) {
        this.serviceLevelNotices = []
        if (this.awaitingGa4 && !this.isLookupPending && this.modelValue?.length === 7) {
          this.awaitingGa4 = false
        }
        return
      }
      const zip = normalizeZipDigits(this.modelValue || '')
      if (zip.length !== 7 || normalizeZipDigits(master.zip || '') !== zip) {
        return
      }
      this.isConfirmingZip = false
      if (this.isServiceLevel) {
        this.setServiceLevelNotifications()
      } else if (this.awaitingGa4) {
        this.awaitingGa4 = false
        this.sendGA4EventInputZipCode(zip)
      }
    },
    sendGA4EventInputZipCode(value: string) {
      const addressAndMessage: YhcZipCodePrefectureType = {
        zipCode: value,
        prefecture: this.prefecture,
        areaMessage: this.errorMessage || this.validMessage,
      }
      if (this.fromOrTo === 'from') {
        $yhcGA4EventSender.inputZipFrom(addressAndMessage)
      } else {
        $yhcGA4EventSender.inputZipTo(addressAndMessage)
      }
    },
    setServiceLevelNotifications() {
      this.serviceLevelNotices = [] // 初期化
      const fgAndOptionNotAvailable =
        '※サイズFGランクの配送及びオプションのご利用はできません。'
      const fgAndOptionAvailableArtSetting =
        '※サイズFGランクもしくはオプション利用時はアートセッティングデリバリー担当となります。'
      // お預かり先
      if (this.fromDeliveryAreaFlg === '2') {
        this.serviceLevelNotices.push({
          fromTo: 'from',
          message: fgAndOptionNotAvailable,
        })
      } else if (this.fromDeliveryAreaFlg === '0') {
        this.serviceLevelNotices.push({
          fromTo: 'from',
          message: fgAndOptionAvailableArtSetting,
        })
      }
      // お届け先
      if (this.toDeliveryAreaFlg === '2') {
        this.serviceLevelNotices.push({ fromTo: 'to', message: fgAndOptionNotAvailable })
      } else if (this.toDeliveryAreaFlg === '0') {
        this.serviceLevelNotices.push({
          fromTo: 'to',
          message: fgAndOptionAvailableArtSetting,
        })
      }
    },
  },
  computed: {
    $yhcefs(): YhcEstimateFormFrontService {
      return this.YhcEstimateFormFrontServiceInstance
    },
    fromDeliveryAreaFlg(): string {
      return this.$yhcefs.$userInputData.zip_from_yhc_master?.deliveryAreaFlag
    },
    toDeliveryAreaFlg(): string {
      return this.$yhcefs.$userInputData.zip_to_yhc_master?.deliveryAreaFlag
    },
    isLookupPending(): boolean {
      const data = this.$yhcefs?.$userInputData
      return this.fromOrTo === 'from' ? !!data?.zip_from_pending : !!data?.zip_to_pending
    },
    displayZipMaster(): ModelYhcMastersByZipcode | null {
      const master = this.zipCodeMaster
      if (!master) return null
      const input = normalizeZipDigits(this.modelValue || '')
      const masterZip = normalizeZipDigits(master.zip || '')
      if (this.isLookupPending) return null
      if (input.length === 7 && masterZip && input !== masterZip) return null
      return master
    },
    isHidingStaleAddress(): boolean {
      const master = this.zipCodeMaster
      const input = normalizeZipDigits(this.modelValue || '')
      const masterZip = normalizeZipDigits(master?.zip || '')
      const mismatch = input.length === 7 && !!master && !!masterZip && input !== masterZip
      return this.isLookupPending || mismatch
    },
    isAddressLoading(): boolean {
      return (
        (this.modelValue || '').length === 7 &&
        (this.isConfirmingZip || this.isLookupPending || this.isHidingStaleAddress)
      )
    },
    /**
     * エラーメッセージを表示する
     */
    errorMessage() {
      if (!this.modelValue?.length) {
        return '入力必須です'
      }
      if (this.modelValue.length !== 7) {
        return '7桁で入力してください'
      }
      if (this.isAddressLoading || this.isHidingStaleAddress) {
        return ''
      }
      if (!this.displayZipMaster) {
        return '郵便番号が存在しません'
      }
      if (this.displayZipMaster.deliveryAreaFlag == 3) {
        return 'サービスエリア外です'
      }
      if (
        this.$yhcefs.isFleaMarket &&
        (this.displayZipMaster.deliveryAreaFlag == 0 ||
          this.displayZipMaster.deliveryAreaFlag == 2)
      ) {
        // サービスレベル検索 フリマ用ページの場合は0と2もサービスエリア外とする
        return 'サービスエリア外です'
      }
      return ''
    },
    validMessage() {
      if (this.displayZipMaster?.deliveryAreaFlag == 2) {
        return '大型家財は対象外となります'
      }
      return ''
    },
    addressString() {
      if (!this.displayZipMaster) {
        return ''
      }
      const area = yhcAreasByCode[this.displayZipMaster.prefCode]
      const pref = area?.prefName || area?.name || ''
      return [pref, this.displayZipMaster.city, this.displayZipMaster.town].join(' ')
    },
    prefecture() {
      if (!this.displayZipMaster) {
        return ''
      }
      const area = yhcAreasByCode[this.displayZipMaster.prefCode]
      return area?.prefName || area?.name || ''
    },
    isOutOfServiceArea(): boolean {
      if (this.displayZipMaster?.deliveryAreaFlag == 3) {
        return true
      }
      if (
        this.$yhcefs.isFleaMarket &&
        (this.displayZipMaster?.deliveryAreaFlag == 0 ||
          this.displayZipMaster?.deliveryAreaFlag == 2)
      ) {
        return true
      }
      return false
    },
    isRemoteIsland(): boolean {
      return this.displayZipMaster?.isRemoteIsland === true
    },
    serviceLevelNotification(): string {
      if (!this.serviceLevelNotices || this.serviceLevelNotices.length === 0) {
        return ''
      }
      // サービスレベル検索 フリマ用ページの場合は0と2もサービスエリア外となるので、ここでのNoticeは不要
      if (
        this.$yhcefs.isFleaMarket &&
        (this.displayZipMaster?.deliveryAreaFlag == 0 ||
          this.displayZipMaster?.deliveryAreaFlag == 2)
      ) {
        return ''
      }
      let notices = ''
      for (const notice of this.serviceLevelNotices) {
        if (notice.fromTo === this.fromOrTo) {
          if (notices) {
            notices += '<br>'
          }
          notices += notice.message
        }
      }
      return notices
    },
  },
}
</script>
